import { LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, inject, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  DEFAULT_TOASTER_LIFE,
  ModalBuilder,
  StepperSuccessModal,
  WizardCancelConfirmationOptions,
  ZendraLocalizationServices,
  ZnStepperWizard,
  ZnWizardHost,
} from '@zendra/shared';
import { DistributionService, FlowTypeService } from 'cbd-proxies/planning/proxy';
import type {
  FinalizeStandPartitionInputDto,
  StandPartitionAreaMaintenanceInputDto,
  StandPartitionResultingStandInputDto,
} from 'orchestrator';
import { filter, finalize, forkJoin, map, switchMap } from 'rxjs';
import { COMPLETE_STEPPER_MESSAGE, STANDDIVISION_CONFIG } from './stand-division.config';
import type { IAreaMaintenanceFormValue } from './interfaces/IAreaMaintenanceFormValue';
import type { IStepCreateAreaMaintenancesData } from './interfaces/IStepCreateAreaMaintenancesData';
import type { IStepDefineResultingStandsData } from './interfaces/IStepDefineResultingStandsData';
import type { IStepDescriptionAndCompletionData } from './interfaces/IStepDescriptionAndCompletionData';
import type { IStepSelectStandToDivideData } from './interfaces/IStepSelectStandToDivideData';
import { StandPartitionPlanningService } from './services/stand-partition-planning.service';
import { StepCreateAreaMaintenances } from './step-create-area-maintenances/step-create-area-maintenances';
import type { StepDefineAttributeInheritanceData } from './step-define-attribute-inheritance/step-define-attribute-inheritance.models';
import { StepDefineAttributeInheritance } from './step-define-attribute-inheritance/step-define-attribute-inheritance';
import { StepDefineResultingStands } from './step-define-resulting-stands/step-define-resulting-stands';
import { StepDescriptionAndCompletion } from './step-description-and-completion/step-description-and-completion';
import { StepSelectStandToDivide } from './step-select-stand-to-divide/step-select-stand-to-divide';

const FLOW_TYPE_NAME = 'Partición';
const NO_APPLICABLE_DISTRIBUTION_NAME = 'No aplica';
const PENDING_DISTRIBUTION_NAME = 'Pendiente';
const CATALOG_PAGE_SIZE = 10;

const STEP_COMPONENTS = [
  { id: 1, titleKey: 'StepSelectStandToDivide.Title', component: StepSelectStandToDivide },
  { id: 2, titleKey: 'StepDefineResultingStands.Title', component: StepDefineResultingStands },
  { id: 3, titleKey: 'StepCreateAreaMaintenances.Title', component: StepCreateAreaMaintenances },
  {
    id: 4,
    titleKey: 'StepDefineAttributeInheritance.Title',
    component: StepDefineAttributeInheritance,
  },
  {
    id: 5,
    titleKey: 'StepDescriptionAndCompletion.Title',
    component: StepDescriptionAndCompletion,
  },
] as const;

@Component({
  selector: 'lib-stand-division',
  standalone: true,
  imports: [ZnStepperWizard, StepperSuccessModal],
  templateUrl: './stand-division.html',
})
export class StandDivision extends ZnWizardHost {
  private readonly completionDestroyRef = inject(DestroyRef);
  private readonly distributionService = inject(DistributionService);
  private readonly flowTypeService = inject(FlowTypeService);
  private readonly standDivisionLocalizationService = inject(LocalizationService);
  private readonly completionModalBuilder = inject(ModalBuilder);
  private readonly standRouter = inject(Router);
  private readonly standPartitionService = inject(StandPartitionPlanningService);
  private readonly toaster = inject(ToasterService);

  protected readonly wizard = viewChild(ZnStepperWizard);
  protected readonly storageKey = 'geographic.stand-division';
  protected readonly finalActionLabelKey = 'Geographic::StandDivision.FinalizeAction';

  private submitting = false;

  constructor() {
    super();
    this.initializeStepperWizard(
      STANDDIVISION_CONFIG.localizationKey,
      STEP_COMPONENTS,
      COMPLETE_STEPPER_MESSAGE,
      'Geographic::StandDivision.SuccessMessage',
    );
  }

  protected getWizard(): ZnStepperWizard | undefined {
    return this.wizard();
  }

  protected override onBeforeRedirect(): void {
    this.discardWizardState();
  }

  protected override onSubmitWizard(data: Record<number, unknown>): void {
    if (this.submitting) {
      return;
    }

    this.submitting = true;
    const modalRef = this.completionModalBuilder.openModal(
      COMPLETE_STEPPER_MESSAGE.component,
      COMPLETE_STEPPER_MESSAGE,
    );

    modalRef.closed
      .pipe(
        filter((confirm: boolean) => confirm),
        switchMap(() => this.resolveCatalogIds()),
        map(catalogIds => this.buildFinalizeInput(data, catalogIds)),
        switchMap(input => this.standPartitionService.finalize(input)),
        finalize(() => {
          this.submitting = false;
        }),
        takeUntilDestroyed(this.completionDestroyRef),
      )
      .subscribe({
        next: () => {
          this.successModalVisible.set(true);
        },
        error: () => {
          this.toaster.error(
            `${ZendraLocalizationServices.Geographic}::StandDivision.FinalizationError`,
            '',
            { life: DEFAULT_TOASTER_LIFE },
          );
        },
      });
  }

  protected override async onSuccessModalVisibleChange(isVisible: boolean): Promise<void> {
    this.successModalVisible.set(isVisible);
    if (!isVisible) {
      this.onBeforeRedirect();
      await this.standRouter.navigateByUrl('/geographic/stand');
    }
  }

  public discardStateForHomeNavigation(): void {
    this.discardWizardState();
  }

  protected override getCancelConfirmationOptions(): WizardCancelConfirmationOptions {
    return {
      messageKey: 'LanguageService::ConfirmCancelChangesHarvestPreparation',
      titleKey: 'LanguageService::ConfirmCancelChangesTitleHarvestPreparation',
    };
  }

  private discardWizardState(): void {
    this.wizard()?.discardState();
  }

  private resolveCatalogIds() {
    return forkJoin({
      flowTypes: this.flowTypeService.getList({
        name: FLOW_TYPE_NAME,
        maxResultCount: CATALOG_PAGE_SIZE,
      }),
      noApplicableDistributions: this.distributionService.getList({
        name: NO_APPLICABLE_DISTRIBUTION_NAME,
        maxResultCount: CATALOG_PAGE_SIZE,
      }),
      pendingDistributions: this.distributionService.getList({
        name: PENDING_DISTRIBUTION_NAME,
        maxResultCount: CATALOG_PAGE_SIZE,
      }),
    }).pipe(
      map(result => ({
        flowTypeId: this.findCatalogId(result.flowTypes.items, FLOW_TYPE_NAME),
        noApplicableDistributionId: this.findCatalogId(
          result.noApplicableDistributions.items,
          NO_APPLICABLE_DISTRIBUTION_NAME,
        ),
        pendingDistributionId: this.findCatalogId(
          result.pendingDistributions.items,
          PENDING_DISTRIBUTION_NAME,
        ),
      })),
    );
  }

  private buildFinalizeInput(
    data: Record<number, unknown>,
    catalogIds: Pick<
      FinalizeStandPartitionInputDto,
      'flowTypeId' | 'noApplicableDistributionId' | 'pendingDistributionId'
    >,
  ): FinalizeStandPartitionInputDto {
    const selection = data[1] as IStepSelectStandToDivideData | undefined;
    const resultingStands = data[2] as IStepDefineResultingStandsData | undefined;
    const areaMaintenances = data[3] as IStepCreateAreaMaintenancesData | undefined;
    const inheritance = data[4] as StepDefineAttributeInheritanceData | undefined;
    const completion = data[5] as IStepDescriptionAndCompletionData | undefined;

    if (
      !selection?.standId ||
      !resultingStands ||
      !areaMaintenances ||
      !inheritance ||
      !completion?.transformationReason?.trim()
    ) {
      throw new Error(
        this.standDivisionLocalizationService.instant(
          'Geographic::StandDivision.Errors.IncompleteWizardData',
        ),
      );
    }

    return {
      originalStandId: selection.standId,
      description: completion.transformationReason.trim(),
      ...catalogIds,
      originalAreaMaintenance: this.mapAreaMaintenance(
        areaMaintenances.originalStand.maintenance,
      ),
      newStandAreaMaintenance: this.mapAreaMaintenance(
        areaMaintenances.newStands.maintenance,
      ),
      stands: this.buildResultingStands(resultingStands, inheritance),
    };
  }

  private buildResultingStands(
    resultingStands: IStepDefineResultingStandsData,
    inheritance: StepDefineAttributeInheritanceData,
  ): StandPartitionResultingStandInputDto[] {
    const configuredStands = inheritance.resultingStands.map(row =>
      this.mapResultingStand(row),
    );
    if (configuredStands.some(stand => stand.isOriginal)) {
      return configuredStands;
    }

    const originalStand = resultingStands.rows.find(row => row.type === 'original');
    if (!originalStand) {
      throw new Error(
        this.standDivisionLocalizationService.instant(
          'Geographic::StandDivision.Errors.OriginalResultingStandMissing',
        ),
      );
    }

    return [
      {
        name: originalStand.name,
        area: originalStand.area,
        isOriginal: true,
      },
      ...configuredStands,
    ];
  }

  private mapAreaMaintenance(
    maintenance: IAreaMaintenanceFormValue,
  ): StandPartitionAreaMaintenanceInputDto {
    return {
      periodId: this.toRequiredNumber(maintenance.period),
      adjustmentReasonId: this.toRequiredNumber(maintenance.reason),
      standAreaMaintenanceTypeId: this.toRequiredNumber(maintenance.adjustmentType),
      notes: maintenance.details.trim(),
      isAreaUpdated: maintenance.isAreaUpdated,
    };
  }

  private mapResultingStand(
    row: StepDefineAttributeInheritanceData['resultingStands'][number],
  ): StandPartitionResultingStandInputDto {
    const configuration = row.configuration;

    return {
      name: row.name,
      area: row.area,
      isOriginal: row.id === 'original-stand',
      ...(!configuration
        ? {}
        : {
            standTypeId: this.toOptionalNumber(configuration.standTypeId),
            hasNearbyPopulation:
              configuration.nearbyPopulation === null
                ? undefined
                : configuration.nearbyPopulation === 'true',
            rotationValue: this.toOptionalNumber(configuration.rotation),
            plantationDate: this.toIsoDate(configuration.plantingDate),
            plantationTypeId: this.toOptionalNumber(configuration.plantingTypeId),
            geneticMaterialId: this.toOptionalNumber(configuration.geneticMaterialId),
            seasonId: this.toOptionalNumber(configuration.season),
            contractorId: this.toOptionalNumber(configuration.contractor),
            standStateId: this.toOptionalNumber(configuration.standState),
          }),
    };
  }

  private findCatalogId(
    items: Array<{ id?: number; name?: string }>,
    expectedName: string,
  ): number {
    const normalizedExpectedName = this.normalizeCatalogName(expectedName);
    const item = items.find(
      candidate => this.normalizeCatalogName(candidate.name) === normalizedExpectedName,
    );

    if (item?.id === undefined) {
      throw new Error(
        `${this.standDivisionLocalizationService.instant('Geographic::StandDivision.Errors.CatalogValueNotFound')}: ${expectedName}`,
      );
    }

    return item.id;
  }

  private normalizeCatalogName(value: string | undefined): string {
    return (value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLocaleLowerCase('es');
  }

  private toRequiredNumber(value: string | null): number {
    const parsed = this.toOptionalNumber(value);
    if (parsed === undefined) {
      throw new Error(
        this.standDivisionLocalizationService.instant(
          'Geographic::StandDivision.Errors.RequiredNumericValueMissing',
        ),
      );
    }
    return parsed;
  }

  private toOptionalNumber(value: string | null | undefined): number | undefined {
    if (value === null || value === undefined || value.trim() === '') {
      return undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private toIsoDate(value: string | null | undefined): string | undefined {
    const normalizedValue = value?.trim();
    if (!normalizedValue) {
      return undefined;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) {
      return normalizedValue;
    }

    const localDateParts = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(normalizedValue);
    if (!localDateParts) {
      throw new Error(
        this.standDivisionLocalizationService.instant(
          'Geographic::StandDivision.Errors.InvalidPlantationDateFormat',
        ),
      );
    }

    const [, day, month, year] = localDateParts;
    const parsedDate = new Date(`${year}-${month}-${day}T00:00:00Z`);
    if (
      Number.isNaN(parsedDate.getTime()) ||
      parsedDate.getUTCFullYear() !== Number(year) ||
      parsedDate.getUTCMonth() + 1 !== Number(month) ||
      parsedDate.getUTCDate() !== Number(day)
    ) {
      throw new Error(
        this.standDivisionLocalizationService.instant(
          'Geographic::StandDivision.Errors.InvalidPlantationDate',
        ),
      );
    }

    return `${year}-${month}-${day}`;
  }
}
